<template>
  <!-- HEADER -->
  <div class="header">
    <div class="container-fluid">
      <!-- Card -->
      <div class="card mt-5">
        <div class="card-header border-bottom-0 pb-0">
          <div class="row align-items-center">
            <div class="col">
              <!-- Title -->
              <h4 class="card-header-title mb-0">Course Catalog</h4>
            </div>
            <div class="col-auto">
              <b-button
                class="btn-pill me-2"
                variant="outline-info"
                size="sm"
                @click="downloadTemplate()"
                :disabled="isDownloading">
                <span v-if="isDownloading">
                  <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Downloading...
                </span>
                <span v-else>
                  <span class="fe fe-download"></span>
                  Download Template
                </span>
              </b-button>
              <b-button
                class="btn-pill me-2"
                variant="success"
                size="sm"
                @click="showUploadModal()">
                <span class="fe fe-upload"></span>
                Upload File
              </b-button>
              <b-button
                class="btn-pill me-2"
                variant="outline-info"
                size="sm"
                @click="downloadHistoryCatalogTemplate()"
                :disabled="isDownloadingHistoryCatalog">
                <span v-if="isDownloadingHistoryCatalog">
                  <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Downloading...
                </span>
                <span v-else>
                  <span class="fe fe-download"></span>
                  History Template
                </span>
              </b-button>
              <b-button
                class="btn-pill me-2"
                variant="outline-success"
                size="sm"
                @click="showHistoryUploadModal()">
                <span class="fe fe-upload"></span>
                Upload History Catalog
              </b-button>
              <b-button
                class="btn-pill"
                variant="danger"
                size="sm"
                @click="addData()">
                <span class="fe fe-plus"></span>
                Add Course
              </b-button>
            </div>
          </div>
        </div>

        <!-- All Course Section -->
        <div class="card-body pt-2">
          <!-- Filter Bar -->
          <div class="row align-items-center mb-4">
            <div class="col-md-2">
              <h6 class="mb-0 font-weight-bold">All Course</h6>
            </div>
            <div class="col-md-10">
              <div class="d-flex justify-content-end align-items-center">
                <div class="me-2" style="width: 250px;">
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control form-control-sm"
                    placeholder="Search Course"
                    @input="onSearch" />
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger position-relative"
                  @click="showFilterModal">
                  <span class="fe fe-sliders"></span> Filter
                  <span 
                    v-if="activeFiltersCount > 0" 
                    class="badge bg-danger position-absolute top-0 start-100 translate-middle rounded-pill"
                    style="font-size: 0.65rem; padding: 0.2rem 0.4rem;">
                    {{ activeFiltersCount }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Active Filters Display -->
          <div v-if="activeFiltersCount > 0" class="mb-3">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <small class="text-muted">Active Filters:</small>
              
              <span v-if="filterModal.training_scope" class="badge bg-light text-dark border">
                Training Scope: {{ filterModal.training_scope }}
                <button 
                  type="button" 
                  class="btn-close btn-close-sm ms-1" 
                  @click="clearSingleFilter('training_scope')"
                  style="font-size: 0.6rem; padding: 0.1rem;"></button>
              </span>
              
              <span v-if="filterModal.platform" class="badge bg-light text-dark border">
                Platform: {{ filterModal.platform }}
                <button 
                  type="button" 
                  class="btn-close btn-close-sm ms-1" 
                  @click="clearSingleFilter('platform')"
                  style="font-size: 0.6rem; padding: 0.1rem;"></button>
              </span>
              
              <span v-if="filterModal.role_competency_id" class="badge bg-light text-dark border">
                Role Competency ID: {{ filterModal.role_competency_id }}
                <button 
                  type="button" 
                  class="btn-close btn-close-sm ms-1" 
                  @click="clearSingleFilter('role_competency_id')"
                  style="font-size: 0.6rem; padding: 0.1rem;"></button>
              </span>
              
              <span v-if="filterModal.competency_type" class="badge bg-light text-dark border">
                Competency Type: {{ filterModal.competency_type }}
                <button 
                  type="button" 
                  class="btn-close btn-close-sm ms-1" 
                  @click="clearSingleFilter('competency_type')"
                  style="font-size: 0.6rem; padding: 0.1rem;"></button>
              </span>
              
              <span v-if="filterModal.competency_id" class="badge bg-light text-dark border">
                Competency: {{ filterModal.competency_id }}
                <button 
                  type="button" 
                  class="btn-close btn-close-sm ms-1" 
                  @click="clearSingleFilter('competency_id')"
                  style="font-size: 0.6rem; padding: 0.1rem;"></button>
              </span>
              
              <button 
                type="button" 
                class="btn btn-sm btn-link text-danger p-0 ms-2"
                @click="clearFilters">
                Clear All
              </button>
            </div>
          </div>

        <!-- Loading State -->
        <div v-if="isLoadingCourses" class="text-center py-5">
          <div class="spinner-border text-danger" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 text-muted">Loading courses...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="courseData.length === 0" class="text-center py-5">
          <i class="fe fe-inbox" style="font-size: 3rem; color: #dee2e6;"></i>
          <p class="mt-3 text-muted">No courses found</p>
        </div>

        <!-- Cards Grid -->
        <div v-if="courseData">
          <div class="row">
            <div class="col-lg-3 col-md-4 col-sm-6 mb-4" v-for="course in courseData" :key="course.id">
              <div class="card h-100 shadow-sm border"  @click="showCourseDetail(course)" style="cursor: pointer;">
                <!-- Card Image/Thumbnail -->
                <div class="position-relative">
                  <img 
                    :src="`${imageBaseUrl}/attachments/${course.cover_image_path}`" 
                    :alt="course.title"
                    class="card-img-top"
                    style="height: 160px; object-fit: cover;"
                    @error="handleImageError">
                  
                  <!-- Schedule Status Badge (Top Left) -->
                  <div class="position-absolute" style="top: 8px; left: 8px;">
                    <span :class="getScheduleBadgeClass(course?.training_status)">
                      {{ course?.training_status || '-' }}
                    </span>
                  </div>

                  <div v-if="!(course.is_active)" class="position-absolute" style="bottom: 8px; left: 8px;">
                    <span class="badge bg-warning">
                      Draft
                    </span>
                  </div>
                  
                  <!-- Bookmark Icon (Top Right) -->
                  <div class="position-absolute" style="top: 8px; right: 8px;">
                    <div class="bg-white rounded shadow-sm p-1" style="display: flex; align-items: center; justify-content: center;">
                      <img 
                        :src="`${imageBaseUrl}/attachments/${course.provider_logo_path}`" 
                        :alt="course.provider_name"
                        style="height: 16px; object-fit: cover;"
                        @error="handleImageProviderError">
                    </div>
                  </div>
                </div>
                
                <!-- Card Body -->
                <div class="card-body p-3">
                  <!-- Badges Row -->
                  <div class="mb-2 d-flex flex-wrap gap-1">
                    <span class="badge-icon bg-danger-soft text-danger">
                      <i :class="getTrainingTypeIcon(course.training_type)" style="font-size: 10px;"></i>
                    </span>
                    <span class="badge-custom bg-warning-soft text-warning">
                      {{ course.role_competency || '-' }}
                    </span>
                    <span class="badge-custom bg-info-soft text-info">
                      {{ course.role_competency_type || '-' }}
                    </span>
                  </div>
                  
                  <!-- Course Title -->
                  <h6 class="card-title font-weight-bold mb-2" style="font-size: 0.95rem; line-height: 1.3;">
                    {{ course.title || '-' }}
                  </h6>

                  <!-- Course Description -->
                  <p class="card-text text-muted text-truncate" style="max-width: 100%; font-size: 0.75rem; line-height: 1.4;">
                    {{ course.description || '-' }}
                  </p>
                </div>
                
                <!-- Card Footer -->
                <div class="card-footer bg-white border-top px-3 py-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <!-- Left Side: Stats -->
                    <div class="d-flex align-items-center text-muted" style="font-size: 0.75rem;">
                      <div class="d-flex align-items-center me-3">
                        <i class="fe fe-clock me-1"></i>
                        <span>{{ Number(course.hour) || '-' }}hrs</span>
                      </div>
                      <div class="d-flex align-items-center me-3">
                        <i class="fe fe-dollar-sign me-1"></i>
                        <span>IDR {{ compactNumberFormatter(course.closest_batch_total_price) || '-' }}</span>
                      </div>
                      <div class="d-flex align-items-center">
                        <i class="fe fe-users me-1"></i>
                        <span>
                          {{
                            course.closest_batch_participant_count !== null &&
                            course.closest_batch_participant_count !== undefined
                              ? course.closest_batch_participant_count
                              : "-"
                          }}/{{ course.closest_batch_quota || '-' }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Right Side: Dropdown Menu -->
                    <div class="dropdown" @click.stop>
                      <button 
                        class="btn btn-sm btn-link text-muted p-0" 
                        type="button" 
                        :id="'dropdown-' + course.id"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style="text-decoration: none;">
                        <i class="fe fe-more-vertical" style="font-size: 1.1rem;"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end" :aria-labelledby="'dropdown-' + course.id">
                        <li>
                          <a 
                            class="dropdown-item" 
                            href="#" 
                            @click.prevent="editCourseFromList(course)">
                            <i class="fe fe-edit me-2"></i>Edit
                          </a>
                        </li>
                        <li>
                          <a 
                            class="dropdown-item" 
                            href="#" 
                            @click.prevent="viewData(course)">
                            <i class="fe fe-file me-2"></i>View Detail
                          </a>
                        </li>
                        <li>
                          <a 
                            class="dropdown-item" 
                            href="#" 
                            @click.prevent="cloneCourseTemplate(course)">
                            <i class="fe fe-copy me-2"></i>Clone Template
                          </a>
                        </li>
                        <li>
                          <a 
                            class="dropdown-item text-danger" 
                            href="#" 
                            @click.prevent="deleteFromList(course)">
                            <i class="fe fe-trash me-2"></i>Delete
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="mb-3">
            <i class="fe fe-inbox" style="font-size: 48px; color: #6c757d;"></i>
          </div>
          <h5 class="text-muted">No courses found</h5>
          <p class="text-muted">Try adjusting your search criteria or add a new course.</p>
        </div>

        <!-- Pagination Footer -->
        <div v-if="!isLoading && courseData.length > 0" class="mt-4 pt-3 border-top">
          <div class="row align-items-center">
            <div class="col-md-4">
              <div class="d-flex align-items-center">
                <span class="text-muted small me-2">Show</span>
                <select 
                  v-model="body.limit" 
                  class="form-select form-select-sm" 
                  style="width: 70px;"
                  @change="onPerPageChange">
                  <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                </select>
                <span class="text-muted small ms-2">entries</span>
              </div>
            </div>
            <div class="col-md-4 text-center">
              <p class="text-muted small mb-0">
                Showing {{ ((body.page - 1) * body.limit) + 1 }} to {{ Math.min(body.page * body.limit, coursesTotalItems) }} of {{ coursesTotalItems }} entries
              </p>
            </div>
            <div class="col-md-4">
              <nav aria-label="Course pagination">
                <ul class="pagination pagination-sm justify-content-end mb-0">
                  <!-- Previous Button -->
                  <li class="page-item" :class="{ disabled: body.page <= 1 }">
                    <button 
                      class="page-link" 
                      @click="changePage(body.page - 1)"
                      :disabled="body.page <= 1">
                      <i class="fe fe-chevron-left"></i>
                    </button>
                  </li>
                  
                  <!-- Page Numbers -->
                  <template v-for="(page, index) in visiblePages">
                    <!-- Show dots -->
                    <li 
                      v-if="page === '...'" 
                      :key="'dots-' + index"
                      class="page-item disabled">
                      <span class="page-link">...</span>
                    </li>
                    <!-- Show page number -->
                    <li 
                      v-else
                      :key="page"
                      class="page-item"
                      :class="{ active: page === body.page }">
                      <button 
                        class="page-link" 
                        @click="changePage(page)">
                        {{ page }}
                      </button>
                    </li>
                  </template>
                  
                  <!-- Next Button -->
                  <li class="page-item" :class="{ disabled: body.page >= totalPages }">
                    <button 
                      class="page-link" 
                      @click="changePage(body.page + 1)"
                      :disabled="body.page >= totalPages">
                      <i class="fe fe-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <!-- Course Type Filter Section -->
        <div class="mt-4 pt-4 border-top">
          <h6 class="mb-3 font-weight-bold">Course Type</h6>
          <div class="d-flex gap-3 flex-wrap">
            <!-- Certification -->
            <div class="course-type-filter" @click="filterByCourseType('certification')">
              <div class="d-flex align-items-center gap-2">
                <span class="badge-icon bg-danger-soft text-danger">
                  <i class="fe fe-file-text"></i>
                </span>
                <span class="text-dark">Certification</span>
              </div>
            </div>

            <!-- Semi Certification -->
            <div class="course-type-filter" @click="filterByCourseType('semi-certification')">
              <div class="d-flex align-items-center gap-2">
                <span class="badge-icon bg-danger-soft text-danger">
                  <i class="fe fe-shield"></i>
                </span>
                <span class="text-dark">Semi Certification</span>
              </div>
            </div>

            <!-- Training -->
            <div class="course-type-filter" @click="filterByCourseType('training')">
              <div class="d-flex align-items-center gap-2">
                <span class="badge-icon bg-danger-soft text-danger">
                  <i class="fe fe-user"></i>
                </span>
                <span class="text-dark">Training</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modals -->
      <modal-filter
        @reset-filters="resetFilters"
        @apply-filters="applyFilters"
      />

      <modal-course-detail
        :selectedCourse="selectedCourse"
        @edit-course="editCourse"
        @clone-course="cloneCourseTemplate"
        @delete-course="showDeleteConfirmation"
        @open-confirmation-course="openConfirmationCourse"
        @open-confirmation-delete-batch="openConfirmationDeleteBatch"
      />

      <modal-delete-confirmation
        :courseTitle="courseToDelete?.title"
        :courseId="courseToDelete?.id"
        @deleted-success="onDeleteSuccess"
      />

      <modal-confirmation-course
        :batchData="selectedBatch"
        @submitted-success="onConfirmationSuccess"
      />

      <modal-delete-confirmation-batch
        :batchData="selectedBatch"
        @deleted-success="onDeleteSuccess"
      />

      <modal-add-course
        @course-created="onCourseCreated"
        @course-saved="onCourseSaved"
      />

      <!-- Upload Modal -->
      <b-modal 
        id="modal-upload" 
        centered 
        hide-footer 
        title="Upload Course File"
        size="lg">
        <form @submit.prevent="uploadFile()">
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label>Select File <span class="text-danger">*</span></label>
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept=".xlsx"
                  @change="onFileChange"
                  required />
                <small class="text-muted">Accepted format: .xlsx (max 5MB)</small>
              </div>
            </div>
            <div class="col-12" v-if="selectedFile">
              <div class="form-group">
                <label>File Preview:</label>
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <i class="fe fe-file-text text-primary me-2" style="font-size: 2rem;"></i>
                      <div>
                        <p class="mb-0 font-weight-bold">{{ selectedFile.name }}</p>
                        <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-upload')">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="!selectedFile || isUploading">
              <span
                v-if="isUploading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ isUploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </form>
      </b-modal>

    </div>
    </div>
  </div>
</template>

<script>
import logic from './course-catalog.logic.js';
import ModalFilter from './components/ModalFilter.vue';
import ModalCourseDetail from './components/ModalCourseDetail.vue';
import ModalDeleteConfirmation from './components/ModalDeleteConfirmation.vue';
import ModalConfirmationCourse from './components/ModalConfirmationCourse.vue';
import ModalAddCourse from './components/ModalAddCourse.vue';
import ModalDeleteConfirmationBatch from './components/ModalDeleteConfirmationBatch.vue';

export default {
  ...logic,
  components: {
    ModalFilter,
    ModalCourseDetail,
    ModalDeleteConfirmation,
    ModalConfirmationCourse,
    ModalAddCourse,
    ModalDeleteConfirmationBatch,
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

/* Course Card Styles */
.course-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.course-card:hover {
  /* transform: translateY(-2px); */
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.card-title {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: #2d3748;
}

.card-text {
  font-size: 0.875rem;
  color: #718096;
}

/* Badge Styles */
.badge {
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 0.25rem;
}

.badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 50%;
  font-size: 0.65rem;
}

.badge-custom {
  padding: 0.25rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 0.25rem;
}

/* Soft Badge Colors */
.bg-danger-soft {
  background-color: #fce4e4;
}

.text-danger {
  color: #e63757 !important;
}

.bg-warning-soft {
  background-color: #fef5e4;
}

.text-warning {
  color: #f6c343 !important;
}

.bg-info-soft {
  background-color: #e4f4f7;
}

.text-info {
  color: #39afd1 !important;
}

/* Schedule Badge */
.badge-success {
  background-color: #00d97e;
  color: white;
}

.badge-danger {
  background-color: #e63757;
  color: white;
}

.badge-primary {
  background-color: #2c7be5;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

/* Pagination */
.pagination .page-link {
  color: #2c7be5;
  border: 1px solid #dee2e6;
  padding: 0.375rem 0.75rem;
}

.pagination .page-item.active .page-link {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.pagination .page-item.disabled .page-link {
  cursor: default;
  background-color: transparent;
  border-color: transparent;
  color: #6c757d;
  pointer-events: none;
}

.pagination .page-link:hover:not(.disabled) {
  background-color: #f8f9fa;
}

.pagination .page-item.active .page-link:hover {
  background-color: #d32f4f;
  border-color: #d32f4f;
}
</style>
